import React, { useState, createContext, useEffect } from "react";
import { showErrorMessage, showSuccessMessage } from "../helpers/useToast";
import {
  collection,
  addDoc,
  getDocs,
  where,
  query,
  updateDoc,
  deleteDoc,
} from "firebase/firestore";
import { ref, getStorage, uploadBytes, getDownloadURL } from "firebase/storage";
import { db } from "../js/firebase";
import { Fetch } from "../hooks/use-fetch-hook";
import { formatDate } from "../helpers/utils";
import {
  organizationTypes,
  organizationTiers,
  organizationDenominations,
  countries,
  provinces,
} from "../assets/data";
import {
  isValidEmail,
  isValidPhone,
  isValidPostalCode,
  isValidAddress,
  exceedsWordLimit,
  isValidWebsite,
} from "../helpers/organizationValidation";

export const OrganizationContext = createContext(null);

// Define initial state for organization data
const initialOrganizationState = {
  name: "",
  type: "",
  tier: "",
  stripeId: "",
  zohoAccountId: "",
  is_sponsored: false,
  denomination: "",
  paymentTerm: "",
  address: "",
  city: "",
  province: "",
  postalCode: "",
  country: "",
  website: "",
  organizationPhoneNumber: "",
  carePersonName: "",
  carePersonEmail: "",
  carePersonPhone: "",
  assignedNumber: "",
  prayerPartnersCount: "",
  serviceStartTimes: "",
  churchPrograms: "",
  prayerlineUrl: "",
  description: "",
  image: null,
};

// Define initial state for the context
const initialState = {
  organization: { ...initialOrganizationState },
  filterByArray: [],
  filterValueArray: [],
  monthlyOrganizationNames: [
    "Lakepoint Family Church",
    "Temiskaming Church",
    "Agora Network Ministries",
  ],
  monthlyOrganization: [],
  organizations: undefined,
  organizationNumbers: {},
  showAllOrganizations: false,
  nameClicked: "",
  currentPage: 1,
    validationErrors: {},
  itemsPerPage: 10,
  totalOrganizations: 0,
  callMetricsData: null, // Store raw API data directly
};

export const OrganizationProvider = ({ children }) => {
  // Use a single state object instead of multiple useState calls
  const [state, setState] = useState(initialState);

  // Helper function to update state
  const updateState = (updates) => {
    setState((prevState) => ({
      ...prevState,
      ...updates,
    }));
  };

  // Helper function to update organization fields
  const updateOrganizationField = (field, value) => {
    // Trim whitespace for text fields to prevent padding issues
    const processedValue =
      field === "name" ||
      field === "carePersonName" ||
      field === "address" ||
      field === "city"
        ? typeof value === "string"
          ? value.trim()
          : value
        : value;

    setState((prevState) => ({
      ...prevState,
      organization: {
        ...prevState.organization,
        [field]: processedValue,
      },
      validationErrors: {
        ...prevState.validationErrors,
        [field]: "",
      },
    }));
  };

  // Helper function to reset organization
  const resetOrganization = () => {
    setState((prevState) => ({
      ...prevState,
      organization: { ...initialOrganizationState },
      validationErrors: {},
    }));
  };

  // Function to fetch call metrics from API - store raw data directly
  const fetchCallMetrics = async (firebaseId = null, role = null) => {
    try {
      const targetFirebaseId = firebaseId;

      // Build URL with firebaseId
      let url = `${process.env.REACT_APP_API_URL}/org_all_metrics?firebaseId=${targetFirebaseId}&department=Volunteer`;

      // Add role parameter if provided
      if (role) {
        url += `&role=${role}`;
      }

      const options = {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: process.env.REACT_APP_API_AUTHORIZATION,
          Accept: "application/json",
        },
      };

      const result = await Fetch(url, options);

      // Store the raw API response directly
      updateState({ callMetricsData: result });
      return result;
    } catch (error) {
      console.error("Error fetching call metrics:", error);
      return null;
    }
  };

  // Run the get monthly organizations once
  useEffect(() => {
    getMonthlyOrganization();
    fetchCallMetrics(); // Fetch call metrics when component mounts
  }, []);

  // Run every time a new filter option is selected
  useEffect(() => {
    filterOrganizations(state.filterByArray, state.filterValueArray);
  }, [state.filterByArray]);

  // Organizations where payment term is monthly
  const getMonthlyOrganization = async () => {
    const monthlyOrgs = [];
    const orgRef = collection(db, "organizations");
    const q = query(
      orgRef,
      where("name", "in", state.monthlyOrganizationNames),
    );
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
      monthlyOrgs.push({ id: doc.id, ...doc.data() });
    });

    updateState({ monthlyOrganization: monthlyOrgs });
  };

  // Filter the organizations based on the filter conditions
  const filterOrganizations = async (filterBy, filterValue) => {
    var collections = [];
    const conditions = [];
    var organizationNumbers = {};
    let nameSearchValue = null;
    let carePersonNameSearchValue = null;
    let createdAtRange = null;
    let organizationStatusFilter = null;

    // Extract name search if it exists
    const nameIndex = filterBy.indexOf("name");
    if (nameIndex !== -1) {
      nameSearchValue = filterValue[nameIndex];
      // Remove name from filterBy and filterValue arrays
      filterBy = [...filterBy];
      filterValue = [...filterValue];
      filterBy.splice(nameIndex, 1);
      filterValue.splice(nameIndex, 1);
    }

    // Extract carePersonName search if it exists
    const carePersonNameIndex = filterBy.indexOf("carePersonName");
    if (carePersonNameIndex !== -1) {
      carePersonNameSearchValue = filterValue[carePersonNameIndex];
      // Remove carePersonName from filterBy and filterValue arrays
      filterBy = [...filterBy];
      filterValue = [...filterValue];
      filterBy.splice(carePersonNameIndex, 1);
      filterValue.splice(carePersonNameIndex, 1);
    }

    // Extract createdAt date range if it exists
    const createdAtIndex = filterBy.indexOf("createdAt");
    if (createdAtIndex !== -1) {
      createdAtRange = filterValue[createdAtIndex];
      // Remove createdAt from filterBy and filterValue arrays
      filterBy = [...filterBy];
      filterValue = [...filterValue];
      filterBy.splice(createdAtIndex, 1);
      filterValue.splice(createdAtIndex, 1);
    }

    // Organization status is stored in the `status` field and may vary in casing.
    const organizationStatusIndex = filterBy.indexOf("orgActiveStatus");
    if (organizationStatusIndex !== -1) {
      organizationStatusFilter = filterValue[organizationStatusIndex];
      filterBy = [...filterBy];
      filterValue = [...filterValue];
      filterBy.splice(organizationStatusIndex, 1);
      filterValue.splice(organizationStatusIndex, 1);
    }

    // Build conditions for other filters
    filterBy.forEach((filter, index) => {
      if (Array.isArray(filterValue[index])) {
        conditions.push(where(filter, "in", filterValue[index]));
      } else {
        conditions.push(where(filter, "==", filterValue[index]));
      }
    });

    // Add date range conditions if present
    if (createdAtRange && createdAtRange.start && createdAtRange.end) {
      // Convert to JavaScript Date objects if they aren't already
      const startDate =
        createdAtRange.start instanceof Date
          ? createdAtRange.start
          : new Date(createdAtRange.start);

      const endDate =
        createdAtRange.end instanceof Date
          ? createdAtRange.end
          : new Date(createdAtRange.end);

      // Set end date to end of day
      endDate.setHours(23, 59, 59, 999);

      // Add date range conditions
      conditions.push(where("createdAt", ">=", startDate));
      conditions.push(where("createdAt", "<=", endDate));
    }

    // Fetch organizations with filters
    const orgRef = collection(db, "organizations");
    const q =
      conditions.length > 0 ? query(orgRef, ...conditions) : query(orgRef);
    const querySnapshot = await getDocs(q);

    // Process results and apply client-side filters
    querySnapshot.forEach((doc) => {
      const orgData = doc.data();
      let includeOrg = true;

      // Apply name filter if present
      if (nameSearchValue) {
        const orgName = orgData.name?.toLowerCase() || "";
        const searchTerm = nameSearchValue.toLowerCase();

        if (!orgName.includes(searchTerm)) {
          includeOrg = false;
        }
      }

      // Apply care person name filter if present
      if (includeOrg && carePersonNameSearchValue) {
        const carePersonName = orgData.carePersonName?.toLowerCase() || "";
        const searchTerm = carePersonNameSearchValue.toLowerCase();

        if (!carePersonName.includes(searchTerm)) {
          includeOrg = false;
        }
      }

      if (includeOrg && organizationStatusFilter) {
        const selectedStatuses = (Array.isArray(organizationStatusFilter)
          ? organizationStatusFilter
          : [organizationStatusFilter]
        ).map((status) => String(status).toLowerCase());
        const organizationStatus = String(orgData.status || "new").toLowerCase();

        if (!selectedStatuses.includes(organizationStatus)) {
          includeOrg = false;
        }
      }

      // Include organization if it passed all filters
      if (includeOrg) {
        collections.push({ id: doc.id, ...orgData });
        organizationNumbers[orgData.name] = orgData.assignedNumber;
      }
    });

    collections.sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      }),
    );

    // Update state with filtered results
    updateState({
      totalOrganizations: collections.length,
      organizationNumbers: organizationNumbers,
      organizations: collections,
      currentPage: 1, // Reset to first page when filter changes
    });
  };

  // Add organization to the table if all conditions are passed
  const addOrganization = async (setShow, onSuccess) => {
    const org = { ...state.organization };
    const validationErrors = {};

    // Ensure critical text fields are trimmed before processing
    if (org.name) org.name = org.name.trim();
    if (org.carePersonName) org.carePersonName = org.carePersonName.trim();
    if (org.address) org.address = org.address.trim();
    if (org.city) org.city = org.city.trim();
    if (org.zohoAccountId) org.zohoAccountId = org.zohoAccountId.trim();

    if (
      state.organizations &&
      state.organizations.find((organization) => organization.name === org.name)
    ) {
      showErrorMessage(`Organization with name '${org.name}' already exists`);
      return;
    }

      if (
      !org.name ||
      !org.type ||
      !org.address ||
      !org.city ||
      !org.province ||
      !org.postalCode ||
      !org.country ||
      //!org.website ||
      !org.organizationPhoneNumber ||
      !org.carePersonName ||
      !org.carePersonEmail ||
      !org.carePersonPhone ||
      !org.tier ||
      !org.paymentTerm ||
      !org.zohoAccountId
      //!org.stripeId
    ) {
      ["name", "type", "address", "city", "province", "postalCode", "country", "organizationPhoneNumber", "carePersonName", "carePersonEmail", "carePersonPhone", "tier", "paymentTerm", "zohoAccountId"].forEach((field) => {
        if (!org[field]) validationErrors[field] = "This field is required.";
      });
      updateState({ validationErrors });
      showErrorMessage("Some fields are required to complete Sign Up");
      return;
    }

    if (!org.image) {
      updateState({ validationErrors: { image: "Organization logo is required." } });
      showErrorMessage("Organization Logo is required");
      return;
    }

    if (!isValidPhone(org.organizationPhoneNumber)) {
      validationErrors.organizationPhoneNumber = "Enter a valid phone number.";
    }
    if (!isValidPhone(org.carePersonPhone)) {
      validationErrors.carePersonPhone = "Enter a valid phone number.";
    }
    if (Object.keys(validationErrors).length > 0) {
      updateState({ validationErrors });
      showErrorMessage("Enter valid organization and care person phone numbers");
      return;
    }

    if (!isValidEmail(org.carePersonEmail)) {
      updateState({ validationErrors: { carePersonEmail: "Enter a valid email address." } });
      showErrorMessage("Enter a valid care person email address");
      return;
    }

    if (org.website && !isValidWebsite(org.website)) {
      updateState({ validationErrors: { website: "Enter a valid URL." } });
      showErrorMessage("Enter a valid website URL beginning with http:// or https://");
      return;
    }

    if (org.prayerlineUrl && !isValidWebsite(org.prayerlineUrl)) {
      updateState({ validationErrors: { prayerlineUrl: "Enter a valid URL." } });
      showErrorMessage("Enter a valid Prayerline URL");
      return;
    }

    if (org.address && !isValidAddress(org.address)) {
      updateState({ validationErrors: { address: "Enter a complete address with a street number." } });
      showErrorMessage("Enter a valid address");
      return;
    }

    if (!isValidPostalCode(org.postalCode)) {
      updateState({ validationErrors: { postalCode: "Enter a valid postal code." } });
      showErrorMessage("Enter a valid Canadian or US postal code");
      return;
    }

    for (const field of ["serviceStartTimes", "churchPrograms", "description"]) {
      if (exceedsWordLimit(org[field])) {
        updateState({ validationErrors: { [field]: "Maximum 500 words." } });
        showErrorMessage(`${field} must be 500 words or fewer`);
        return;
      }
    }

    // Create the organization document first so we can use its id for a unique logo filename
    const docRef = await addDoc(collection(db, "organizations"), {
      name: org.name,
      type: org.type,
      tier: org.tier,
      zohoAccountId: org.zohoAccountId,
      stripeId: org.is_sponsored ? "" : org.stripeId,
      is_sponsored: !!org.is_sponsored,
      denomination: org.denomination || "",
      paymentTerm: org.paymentTerm,
      address: org.address,
      city: org.city,
      province: org.province,
      country: org.country,
      postalCode: org.postalCode,
      website: org.website,
      organizationPhoneNumber: org.organizationPhoneNumber,
      carePersonName: org.carePersonName,
      carePersonEmail: org.carePersonEmail,
      carePersonPhone: org.carePersonPhone,
      assignedNumber: org.assignedNumber,
      prayerPartnersCount: org.prayerPartnersCount,
      serviceStartTimes: org.serviceStartTimes,
      churchPrograms: org.churchPrograms,
      prayerlineUrl: org.prayerlineUrl || "",
      description: org.description || "",
      createdAt: new Date(),
      // placeholder for logo URL; will be updated after upload
      logoUrl: "",
    });

    // Upload logo to storage using the document id as the filename to avoid collisions
    const storage = getStorage();
    const ext = org.image && org.image.name ? org.image.name.split('.').pop() : 'png';
    const filename = `${docRef.id}.${ext}`;
    const storageRef = ref(storage, `logos/${filename}`);

    try {
      await uploadBytes(storageRef, org.image);
      const downloadUrl = await getDownloadURL(storageRef);
      await updateDoc(docRef, { logoUrl: downloadUrl });
    } catch (uploadError) {
      console.error("Error uploading logo, rolling back organization doc:", uploadError);
      // Remove the created doc to avoid orphan records without a logo
      try {
        await deleteDoc(docRef);
      } catch (delErr) {
        console.error("Failed to delete organization doc after upload failure:", delErr);
      }
      showErrorMessage("Failed to upload organization logo. Organization was not created.");
      return;
    }

    showSuccessMessage("New Organization Added Successfully");
    setShow(false);
    resetOrganization();
    onSuccess?.();
  };

  // Get total monthly calls for all monthly organizations
  const getTotalMonthlyCalls = async () => {
    if (state.monthlyOrganization) {
      var firstDay = new Date(); // current date
      firstDay.setDate(1);
      firstDay.setDate(0); // set to last day of previous month
      let firstDayOfPreviousMonth = new Date(
        firstDay.getFullYear(),
        firstDay.getMonth(),
        1,
      );

      var lastDayOfPreviousMonth = new Date();
      lastDayOfPreviousMonth.setDate(1); // going to 1st of the month
      lastDayOfPreviousMonth.setHours(-1); // removing on hour goes to previous month
      lastDayOfPreviousMonth.setMinutes(59);
      lastDayOfPreviousMonth.setSeconds(59);

      var monthlyOrgs = [];

      await Promise.all(
        state.monthlyOrganization.map(async (organization) => {
          const url = `${
            process.env.REACT_APP_API_URL
          }/org_totals?twilio_number=
                ${encodeURIComponent(
                  organization.assignedNumber,
                )}&start_date=${formatDate(
                  firstDayOfPreviousMonth,
                )}&end_date=${formatDate(lastDayOfPreviousMonth)}`;
          var options = {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: process.env.REACT_APP_API_AUTHORIZATION,
              Accept: "application/json",
            },
          };
          const result = await Fetch(url, options);
          monthlyOrgs.push({
            stripeId: organization.stripeId,
            tier: organization.tier,
            type: organization.type,
            totalMonthlyCalls: result.data.total_calls,
          });
        }),
      );
      return monthlyOrgs;
    }
  };

  // On logo change while creating the organization
  const onImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      updateOrganizationField("image", event.target.files[0]);
    }
  };

  const reFeatch = (updatedOrganization) => {
    console.log("Updated value :" + updatedOrganization.is_premium);
  };

  return (
    <OrganizationContext.Provider
      value={{
        // Organization state and actions
        organization: state.organization,
         validationErrors: state.validationErrors,
        updateOrganizationField,
        resetOrganization,
        addOrganization,
        onImageChange,
        reFeatch,

        // Filter state and actions
        filterByArray: state.filterByArray,
        setFilterByArray: (value) => updateState({ filterByArray: value }),
        filterValueArray: state.filterValueArray,
        setFilterValueArray: (value) =>
          updateState({ filterValueArray: value }),
        filterOrganizations,

        // Organization data
        organizations: state.organizations,
        organizationNumbers: state.organizationNumbers,

        // Monthly organization data
        monthlyOrganization: state.monthlyOrganization,
        getTotalMonthlyCalls,

        // UI state
        showAllOrganizations: state.showAllOrganizations,
        setShowAllOrganizations: (value) =>
          updateState({ showAllOrganizations: value }),
        nameClicked: state.nameClicked,
        setNameClicked: (value) => updateState({ nameClicked: value }),

        // Pagination
        currentPage: state.currentPage,
        setCurrentPage: (value) => updateState({ currentPage: value }),
        itemsPerPage: state.itemsPerPage,
        totalOrganizations: state.totalOrganizations,

        // Call Metrics - Raw data directly
        callMetricsData: state.callMetricsData,
        fetchCallMetrics,
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
};
